import {
  Component,
  OnInit,
  ViewChild,
  inject,
  DestroyRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Banner } from '@shared/entities/banner';
import { ThemeService } from 'src/app/services/theme.service';
import { map, Observable, tap, first, catchError, of, finalize, forkJoin, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { ProfileService } from '@shared/services/api/profile.service';
import { NationsService } from '@shared/services/api/nations.service';
import { FilterOrSortDropdownType, FilterSelectedEventType, Roles } from '@shared/models/types';
import { BannerFormService } from '@shared/services/api/banner-form.service';
import { format } from 'date-fns';
import { ValuesService } from '@shared/services/api/values.service';
import { MachineOperatingSectorService } from '@shared/services/api/machine-operating-sector.service';
import { ArticleTypologyService } from '@shared/services/api/article-typology.service';
import { IcamSelectComponent } from '@shared/components/icam-select/icam-select.component';
import {
  IcamDatepickerComponent,
  DatepickerChangeEvent
} from '@shared/components/icam-datepicker/icam-datepicker.component';
import { Attachment } from '@shared/entities/attachment';

interface NamedOption {
  name: string;
  id: string;
}

@Component({
  selector: 'app-banner-form',
  templateUrl: './banner-form.component.html',
  styleUrl: './banner-form.component.scss'
})
export class BannerFormComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private _formBuilder = inject(FormBuilder);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _bannerFormService = inject(BannerFormService);
  private _themeService = inject(ThemeService);
  private _location = inject(Location);
  public translateService = inject(TranslateService);
  private _profileService = inject(ProfileService);
  private _nationsService = inject(NationsService);
  private _valuesService = inject(ValuesService);
  private _machineOperatingSectorService = inject(MachineOperatingSectorService);
  private _articleTypologyService = inject(ArticleTypologyService);

  form!: FormGroup;
  private _bannerId = '';
  isEdit = false;
  isLight = true;
  isLoading = true;
  errorMessage = '';
  isCurrentUserAdmin = false;
  nationOptions: NamedOption[] = [];
  typologyOptions: NamedOption[] = [];
  operatingSectorOptions: NamedOption[] = [];
  articleTypologyOptions: NamedOption[] = [];
  dateRangeField: FilterOrSortDropdownType;
  dateRangeValue = '';
  rolesField: FilterOrSortDropdownType;
  private savedDateRange: { startDate: string | null; endDate: string | null } = { startDate: null, endDate: null };

  showSuccess = false;
  showError = false;
  saveSuccess = false;
  saveError = false;
  deleteSuccess = false;
  deleteError = false;

  desktopAttachment: File | null = null;
  mobileAttachment: File | null = null;

  existingDesktopAttachment: Attachment | null = null;
  existingMobileAttachment: Attachment | null = null;

  @ViewChild('dateRangePicker') dateRangePicker!: IcamDatepickerComponent;
  @ViewChild('roleSelector') roleSelector!: IcamSelectComponent;

  constructor() {
    this.dateRangeField = { key: 'custom_period', values: [] };
    this.rolesField = {
      key: 'role',
      values: [
        { id: 'partner', value: 'BANNER_FORM.ROLE_PARTNER' },
        { id: 'final_user', value: 'BANNER_FORM.ROLE_FINAL_USER' }
      ]
    };
  }

  ngOnInit(): void {
    this.initializeForm();

    this.form
      .get('machineTypology')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => this.updateArticleTypologyState(value));

    this._route.params
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((params) => {
          this._bannerId = params['id'] || '';
          this.isEdit = !!this._bannerId;
        })
      )
      .subscribe();

    forkJoin({
      theme: this._themeService.isLight$.pipe(first()),
      nations: this.loadNationsData(),
      typologies: this.loadMachineTypologiesData(),
      sectors: this.loadOperatingSectorsData(),
      articleTypologies: this.loadArticleTypologiesData(),
      userRole: this.hasAdminRole().pipe(first())
    })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          if (!this.isEdit) {
            this.isLoading = false;
          }
        })
      )
      .subscribe({
        next: (results) => {
          this.isLight = results.theme;
          this.isCurrentUserAdmin = results.userRole;

          if (!this.isCurrentUserAdmin) {
            this.form.disable();
          }

          if (this.isEdit && this._bannerId) {
            this.loadBannerData();
          }
        },
        error: (err) => {
          console.error('Error loading reference data:', err);
          this.isLoading = false;
          this.errorMessage = 'Failed to load form data';
          this.saveError = true;
        }
      });
  }

  private updateArticleTypologyState(machineTypologyValue: any[]): void {
    const articleTypologyControl = this.form.get('articleTypology');
    if (!articleTypologyControl) return;

    if (!machineTypologyValue || (Array.isArray(machineTypologyValue) && machineTypologyValue.length === 0)) {
      articleTypologyControl.disable();
      articleTypologyControl.setValue(null);
    } else if (articleTypologyControl.disabled) {
      articleTypologyControl.enable();
    }
  }

  private toNamedOptions(items: string[]): NamedOption[] {
    return items.map((item) => ({ name: item, id: item }));
  }

  private loadNationsData(): Observable<boolean> {
    return this._nationsService.getNations().pipe(
      first(),
      map((response) => {
        if (response && !Array.isArray(response)) {
          this.nationOptions = this.toNamedOptions(response.nations || []);
        }
        return true;
      }),
      catchError((error) => {
        console.error('Error loading nations:', error);
        return of(false);
      })
    );
  }

  private loadMachineTypologiesData(): Observable<boolean> {
    return this._valuesService.getMachineTypes().pipe(
      first(),
      map((response) => {
        if (Array.isArray(response.values)) {
          this.typologyOptions = this.toNamedOptions(response.values);
        }
        return true;
      }),
      catchError((error) => {
        console.error('Error loading machine typologies:', error);
        return of(false);
      })
    );
  }

  private loadOperatingSectorsData(): Observable<boolean> {
    return this._machineOperatingSectorService.getOperatingSectors().pipe(
      first(),
      map((response) => {
        if (response) {
          this.operatingSectorOptions = this.toNamedOptions(response.operatingSectors || []);
        }
        return true;
      }),
      catchError((error) => {
        console.error('Error loading operating sectors:', error);
        return of(false);
      })
    );
  }

  private loadArticleTypologiesData(): Observable<boolean> {
    return this._articleTypologyService.getTypologies().pipe(
      first(),
      map((response) => {
        if (response) {
          this.articleTypologyOptions = this.toNamedOptions(response.articleTypologies || []);
        }
        return true;
      }),
      catchError((error) => {
        console.error('Error loading article typologies:', error);
        return of(false);
      })
    );
  }

  private fetchOptions(options: NamedOption[], searchTerm: string): Observable<NamedOption[]> {
    if (!searchTerm || searchTerm.length < 2) return of(options);
    const term = searchTerm.toLowerCase();
    return of(options.filter((o) => o.name.toLowerCase().includes(term)));
  }

  fetchTypologies = (searchTerm: string) => this.fetchOptions(this.typologyOptions, searchTerm);
  fetchNations = (searchTerm: string) => this.fetchOptions(this.nationOptions, searchTerm);
  fetchOperatingSectors = (searchTerm: string) => this.fetchOptions(this.operatingSectorOptions, searchTerm);
  fetchArticleTypologies = (searchTerm: string) => this.fetchOptions(this.articleTypologyOptions, searchTerm);

  private initializeForm(): void {
    this.form = this._formBuilder.group({
      id: [''],
      description: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      geoLocation: [[]],
      machineTypology: [[]],
      operatingSectorList: [[]],
      articleTypology: [''],
      roles: [''],
      url: ['', Validators.required],
      desktopAttachment: [undefined],
      mobileAttachment: [undefined]
    });

    this.form.get('articleTypology')?.disable();
  }

  isAttachmentsValid(): boolean {
    return (
      (!!this.desktopAttachment || !!this.existingDesktopAttachment) &&
      (!!this.mobileAttachment || !!this.existingMobileAttachment)
    );
  }

  onDateRangeChanged(event: DatepickerChangeEvent): void {
    if (event.name !== 'date_period' || !event.selectedValue) return;

    const parts = event.selectedValue.split(' - ');
    if (parts.length !== 2) return;

    const formattedStartDate = format(new Date(parts[1]), 'yyyy-MM-dd');
    const formattedEndDate = format(new Date(parts[0]), 'yyyy-MM-dd');

    this.form.patchValue({ startDate: formattedStartDate, endDate: formattedEndDate });
    this.savedDateRange = { startDate: formattedStartDate, endDate: formattedEndDate };
    this.form.get('startDate')?.markAsTouched();
    this.form.get('endDate')?.markAsTouched();
  }

  onRoleSelected(event: FilterSelectedEventType): void {
    if (event.name === 'role') {
      this.form.patchValue({ roles: event.selectedValue || null });
    }
  }

  private normalizeToOptions(value: string | string[] | undefined | null, optionsLookup: NamedOption[]): NamedOption[] {
    const ids: string[] = Array.isArray(value) ? value : (value ? [value] : []);
    return ids.map((id) => optionsLookup.find((opt) => opt.id === id) || { id, name: id });
  }

  private loadBannerData(): void {
    if (!this._bannerId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this._bannerFormService
      .findByFilter(this._bannerId)
      .pipe(
        switchMap((banner) => (banner ? of(banner) : this._bannerFormService.find(this._bannerId))),
        takeUntilDestroyed(this._destroyRef),
        tap((banner) => {
          if (banner) {
            this.patchFormValues(banner);
            this.isLoading = false;
          } else {
            console.error('No banner data returned');
          }
        }),
        catchError((error) => {
          this.handleError(error, 'BANNER_FORM.ERROR_LOADING_DATA');
          return of(null);
        })
      )
      .subscribe();
  }

  private patchFormValues(banner: Banner): void {
    const geoLocationOptions = this.normalizeToOptions(banner.geoLocation, this.nationOptions);
    const typologyOptions = this.normalizeToOptions(banner.machineTypology, this.typologyOptions);
    const operatingSectorOptions = this.normalizeToOptions(banner.operatingSectorList as string | string[], this.operatingSectorOptions);

    let selectedRole = '';
    if (banner.roles?.length) {
      const hasPartner = banner.roles.includes(Roles.PARTNER);
      const hasCustomer = banner.roles.includes(Roles.CUSTOMER) || banner.roles.includes(Roles.CUSTOMER_PARTNER);
      if (hasPartner && !hasCustomer) selectedRole = 'partner';
      else if (hasCustomer && !hasPartner) selectedRole = 'final_user';
    }

    this.existingDesktopAttachment = banner.desktopAttachment || null;
    this.existingMobileAttachment = banner.mobileAttachment || null;

    this.form.patchValue({
      id: banner.id || '',
      description: banner.description || '',
      startDate: banner.startDate || '',
      endDate: banner.endDate || '',
      geoLocation: geoLocationOptions,
      machineTypology: typologyOptions,
      operatingSectorList: operatingSectorOptions,
      roles: selectedRole,
      url: banner.url || '',
      articleTypology: banner.articleTypology || ''
    });

    if (this.existingDesktopAttachment) {
      this.form.get('desktopAttachment')?.setValidators(null);
      this.form.get('desktopAttachment')?.updateValueAndValidity();
    }

    if (this.existingMobileAttachment) {
      this.form.get('mobileAttachment')?.setValidators(null);
      this.form.get('mobileAttachment')?.updateValueAndValidity();
    }

    if (banner.startDate && banner.endDate) {
      const formattedStart = format(new Date(banner.startDate), 'dd/MM/yyyy');
      const formattedEnd = format(new Date(banner.endDate), 'dd/MM/yyyy');
      this.dateRangeValue = `${formattedStart} - ${formattedEnd}`;
    }

    if (selectedRole) {
      setTimeout(() => this.roleSelector?.setSelectedValue(selectedRole));
    }

    this.updateArticleTypologyState(this.form.get('machineTypology')?.value);
  }

  onDesktopFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.desktopAttachment = input.files[0];
      this.form.get('desktopAttachment')?.setErrors(null);
    }
  }

  onMobileFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.mobileAttachment = input.files[0];
      this.form.get('mobileAttachment')?.setErrors(null);
    }
  }

  removeDesktopAttachment(): void {
    if (this.desktopAttachment) {
      this.desktopAttachment = null;
      this.form.get('desktopAttachment')?.setValue(undefined);
    } else {
      this.existingDesktopAttachment = null;
    }

    this.form.get('desktopAttachment')?.setValidators([Validators.required]);
    this.form.get('desktopAttachment')?.markAsDirty();
    this.form.get('desktopAttachment')?.updateValueAndValidity();
  }

  removeMobileAttachment(): void {
    if (this.mobileAttachment) {
      this.mobileAttachment = null;
      this.form.get('mobileAttachment')?.setValue(undefined);
    } else {
      this.existingMobileAttachment = null;
    }

    this.form.get('mobileAttachment')?.setValidators([Validators.required]);
    this.form.get('mobileAttachment')?.markAsDirty();
    this.form.get('mobileAttachment')?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.form.invalid || !this.isAttachmentsValid()) {
      Object.keys(this.form.controls).forEach((key) => this.form.get(key)?.markAsTouched());

      if (!this.isAttachmentsValid()) {
        if (!this.desktopAttachment && !this.existingDesktopAttachment) {
          this.form.get('desktopAttachment')?.markAsTouched();
        }
        if (!this.mobileAttachment && !this.existingMobileAttachment) {
          this.form.get('mobileAttachment')?.markAsTouched();
        }
      }
      return;
    }

    this.isLoading = true;
    this.resetStateFlags();

    const bannerData = { ...this.form.value };

    ['geoLocation', 'machineTypology', 'operatingSectorList'].forEach((key) => {
      if (Array.isArray(bannerData[key])) {
        bannerData[key] = bannerData[key].map((item: any) => item.id);
      }
    });

    if (bannerData.articleTypology && typeof bannerData.articleTypology === 'object') {
      bannerData.articleTypology = bannerData.articleTypology.id;
    }

    const selectedRole = bannerData.roles;
    if (selectedRole === 'partner') {
      bannerData.roles = [Roles.PARTNER];
    } else if (selectedRole === 'final_user') {
      bannerData.roles = [Roles.CUSTOMER, Roles.CUSTOMER_PARTNER];
    } else {
      bannerData.roles = [];
    }

    delete bannerData.desktopAttachment;
    delete bannerData.mobileAttachment;

    const files: Record<string, File> = {};
    if (this.desktopAttachment) files['desktopAttachment'] = this.desktopAttachment;
    if (this.mobileAttachment) files['mobileAttachment'] = this.mobileAttachment;

    const hasNewAttachments = this.desktopAttachment || this.mobileAttachment;
    let request: Observable<any>;

    if (hasNewAttachments) {
      request = this.isEdit
        ? this._bannerFormService.updateBannerWithAttachments(this._bannerId, bannerData, files)
        : this._bannerFormService.saveBannerWithAttachments(bannerData, files);
    } else {
      request = this.isEdit
        ? this._bannerFormService.editBanner(bannerData, this._bannerId)
        : this._bannerFormService.saveBanner(bannerData);
    }

    this.executeRequest(request);
  }

  private executeRequest(request: Observable<any>): void {
    request
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isLoading = false;

          if (response && (response instanceof Error || response.error)) {
            this.showError = true;
            this.saveError = true;
            this.saveSuccess = false;
            this.errorMessage = this.extractErrorMessage(response);
          } else {
            this.showSuccess = true;
            this.saveSuccess = true;
            this.saveError = false;
          }
        }),
        catchError((error) => {
          this.isLoading = false;
          this.showError = true;
          this.saveError = true;
          this.errorMessage = this.extractErrorMessage(error?.error || error);
          return of(null);
        })
      )
      .subscribe();
  }

  deleteBanner(): void {
    if (!this._bannerId) return;

    this.isLoading = true;
    this.resetStateFlags();

    this._bannerFormService
      .deleteBanner(this._bannerId)
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isLoading = false;

          if (response === true) {
            this.showSuccess = true;
            this.deleteSuccess = true;
          } else {
            this.showError = true;
            this.deleteError = true;
            this.errorMessage = this.extractErrorMessage(response);
          }
        }),
        catchError((error) => {
          this.isLoading = false;
          this.showError = true;
          this.deleteError = true;
          this.errorMessage = this.extractErrorMessage(error?.error || error);
          return of(null);
        })
      )
      .subscribe();
  }

  goToList(): void {
    this._router.navigate(['/banner']);
  }

  private resetStateFlags(): void {
    this.showSuccess = false;
    this.showError = false;
    this.saveSuccess = false;
    this.saveError = false;
    this.deleteSuccess = false;
    this.deleteError = false;
    this.errorMessage = '';
  }

  private extractErrorMessage(response: any): string {
    if (!response) return this.translateService.instant('BANNER_FORM.ERROR_UNKNOWN');

    if (response.message) return response.message;

    if (response.validation?.fields?.length > 0) {
      return `Validation errors:\n${response.validation.fields.map((f: any) => `${f.field}: ${f.error}`).join('\n')}`;
    }

    if (response.error && typeof response.error === 'string') return response.error;

    if (this.saveError) return this.translateService.instant('BANNER_FORM.ERROR_SAVING');
    if (this.deleteError) return this.translateService.instant('BANNER_FORM.ERROR_DELETING');

    return this.translateService.instant('BANNER_FORM.ERROR_UNKNOWN');
  }

  private handleError(error: any, defaultMessage: string): void {
    console.error('Error:', error);
    this.errorMessage = error?.message || this.translateService.instant(defaultMessage);
  }

  hasAdminRole(): Observable<boolean> {
    return this._profileService.role$.pipe(map((role) => role === Roles.ADMIN));
  }

  goBack(): void {
    this._location.back();
  }

  tryAgain(): void {
    this.saveError = false;
    this.showError = false;

    setTimeout(() => {
      if (this.savedDateRange.startDate && this.savedDateRange.endDate && this.dateRangePicker) {
        this.dateRangePicker.setDateRange(this.savedDateRange.startDate, this.savedDateRange.endDate);
      }
    });
  }
}
